/**
 A Polymer address form element that offers suggestions from the Google Maps API as you type.

 Example:

 <cbar-address-autocomplete></cbar-address-autocomplete>

 @demo
 */

import {PolymerElement, html} from "@polymer/polymer";
import '@cbar/google-apis/google-maps-api.js';
import '@cbar/cbar-textfield/cbar-textfield.js';
import '@cbar/localizemixin/LocalizeMixin.js';

class CbarAddressAutocomplete extends Cbar.LocalizeMixin(PolymerElement) {
  static get template() {
    return html`
        <style>
            :host {
                display: block;
                box-sizing: border-box;
            }

            cbar-textfield {
                display: block;
                margin-top: 16px;
                --mdc-theme-primary: var(--cbar-sys-color-primary, #004CA2);
            }
        </style>

<!--        <template is="dom-if" if="[[!useExternalApi]]">-->
            <google-maps-api api-key="[[apiKey]]" client-id="[[clientId]]" version="3.exp" libraries="places"
                on-api-load="_mapsApiLoaded"></google-maps-api>
<!--        </template>-->

        <slot name="title" data-input-id="title">
            <cbar-textfield dense id="titleInput" type="text" label="Location Title" value="[[title]]" data-prop="title"
                on-input="onInputEdited" hidden$="[[noTitle]]"></cbar-textfield>
        </slot>

        <slot name="lookup-address" data-input-id="lookupAddressInput">
            <cbar-textfield dense id="lookupAddressInput" type="text" label="Lookup Address" value="[[lookupAddress]]"
                data-prop="lookupAddress" on-input="onInputEdited"></cbar-textfield>
        </slot>

        <slot name="country" data-input-id="countryInput">
            <cbar-textfield dense id="countryInput" type="text" label="Country" value="[[country]]" data-prop="country"
                on-input="onInputEdited"></cbar-textfield>
        </slot>

        <slot name="postcode" data-input-id="zipCodeInput">
            <cbar-textfield dense id="zipCodeInput" type="text" label="Postcode" value="[[postcode]]"
                data-prop="postcode" on-input="onInputEdited"></cbar-textfield>
        </slot>

        <slot name="state" data-input-id="stateInput">
            <cbar-textfield dense id="stateInput" type="text" label="State" value="[[state]]" data-prop="state"
                on-input="onInputEdited"></cbar-textfield>
        </slot>

        <slot name="suburb" data-input-id="cityInput">
            <cbar-textfield dense id="cityInput" type="text" label="City" value="[[suburb]]" data-prop="suburb"
                on-input="onInputEdited"></cbar-textfield>
        </slot>

        <slot name="street-address" data-input-id="address1Input">
            <cbar-textfield dense id="address1Input" type="text" label="Street Address 1" value="[[streetAddress]]"
                data-prop="streetAddress" on-input="onInputEdited"></cbar-textfield>
        </slot>

        <slot name="street-address-2" data-input-id="address2Input">
            <cbar-textfield dense id="address2Input" type="text" label="Street Address 2" value="[[streetAddress2]]"
                data-prop="streetAddress2" on-input="onInputEdited"></cbar-textfield>
        </slot>

        <slot name="street-address-3" data-input-id="address3Input">
            <cbar-textfield dense id="address3Input" type="text" label="Street Address 3" value="[[streetAddress3]]"
                data-prop="streetAddress3" on-input="onInputEdited"></cbar-textfield>
        </slot>
    `;
  }

  static get properties() {
    return {
      /**
       * A Google Maps API key. To obtain an API key, see developers.google.com/maps/documentation/javascript/tutorial#api_key.
       */
      apiKey: String,
      /**
       * A Google Maps API for Business Client ID. To obtain a Maps API for Business Client ID, see developers.google.com/maps/documentation/business/.
       * If set, a Client ID will take precedence over an API Key.
       */
      clientId: String,
      /**
       * The address to lookup
       */
      lookupAddress: {
        type: String,
        observer: '_initialize',
        notify: true
      },
      title: {
        type: String,
        notify: true
      },
      streetAddress: {
        type: String,
        notify: true
      },
      streetAddress2: {
        type: String,
        notify: true,
      },
      streetAddress3: {
        type: String,
        notify: true,
      },
      state: {
        type: String,
        notify: true
      },
      suburb: {
        type: String,
        notify: true
      },
      postcode: {
        type: String,
        notify: true
      },
      country: {
        type: String,
        notify: true
      },
      useExternalApi: {
        type: Boolean,
        value: false
      },
      optionalFields: {
        type: Array,
        value: [
          'title',
          'address2',
          'address3'
        ],
        observer: "__handleValidFields"
      },
      noTitle: {
        type: Boolean,
        value: false
      },
      place: {
        type: Object,
        value: false
      }
    };
  }

  ready() {
    super.ready();
    this._slotChanged = this._slotChanged.bind(this);

    this.root.querySelectorAll('slot').forEach(slot => {
      slot.addEventListener('slotchange', this._slotChanged);

      // Trigger handler once manually for each slot, as for some
      // browsers, the slotchange event does not get called initially.
      this._saveSlottedNodeToIdHash(slot);
    });
  }

  attached() {
    this._initialize();
  }

  detached() {
    this.autocomplete = null;
  }

  onInputEdited(e) {
    const target = e.target;
    const prop = target.dataset.prop;

    this[prop] = target.value;
  }

  _mapsApiLoaded() {
    this._initialize();
  }

  _initialize() {
    if (!this.isAttached) {
      return;
    }

    if (!(window.google && window.google.maps)) {
      return;
    }

    requestAnimationFrame(() => {

      const input = this._tryGetHtmlInput(this.$.lookupAddressInput);
      if (!(input instanceof HTMLInputElement)) {
        return;
      }

      if (input.__autocomplete) {
        return;
      }

      const options = {types: ['address']};
      this.autocomplete = new window.google.maps.places.Autocomplete(input, options);

      this._boundFillInAddress = this._fillInAddress.bind(this);
      window.google.maps.event.addListener(this.autocomplete, 'place_changed', this._boundFillInAddress);

      input.addEventListener('blur', function () {
        setTimeout(this._boundFillInAddress, 0);
      }.bind(this));

      input.__autocomplete = this.autocomplete;
    })
  }

  _tryGetHtmlInput(input) {
    const originalInput = input;

    while (!(input instanceof HTMLInputElement)) {
      input = input.shadowRoot.querySelector('input');
    }

    return input instanceof HTMLInputElement ? input : originalInput;
  }

  _slotChanged(e) {
    this._saveSlottedNodeToIdHash(e.target);

    // Re-initialize to ensure our listeners are correctly set up.
    this._initialize();
  }

  _saveSlottedNodeToIdHash(slot) {
    let node = slot.assignedNodes()[0];

    if (!node) {
      return;
    }

    let inputId = slot.dataset.inputId;

    this.$[inputId] = node;
  }

  _fillInAddress() {
    let place = this.place = this.autocomplete.getPlace();
    if (!place || !this.lookupAddress) {
      return;
    }

    let subpremise = this.getAddressComponent(place, 'subpremise', 'long_name');
    let streetAddress = '';

    if (subpremise) {
      streetAddress += subpremise + "/";
    }

    this.$.address1Input.value = this.streetAddress = (streetAddress + this.getAddressComponent(place, 'street_number', 'short_name')
      + ' ' + this.getAddressComponent(place, 'route', 'long_name')).trim() || place.name;
    this.$.address2Input.value = this.streetAddress2 = '';
    this.$.address3Input.value = this.streetAddress3 = '';
    this.$.cityInput.value = this.suburb = this.getAddressComponent(place, 'locality', 'long_name');
    this.$.stateInput.value = this.state = this.getAddressComponent(place, 'administrative_area_level_1', 'short_name');
    this.$.zipCodeInput.value = this.postcode = this.getAddressComponent(place, 'postal_code', 'short_name');
    this.$.countryInput.value = this.country = this.getAddressComponent(place, 'country', 'long_name');
  }

  getAddressComponent(place, component, valueType) {
    if (place.address_components) {
      for (var i = 0; i < place.address_components.length; i++) {
        var componentType = place.address_components[i].types[0];
        if (componentType === component) {
          return place.address_components[i][valueType];
        }
      }
    }
    return '';
  }

  /**
   * Validate inputs that arent shown to be optional parsed in optionalFields
   * @return {boolean}
   */
  validate() {
    // default value
    let isValid = true;
    let inputs = Object.values(this.$);
    // convery the nodeList to array and slice the first to remove the lookup element as it shouldnt be required
    inputs = [].slice.call(inputs, 1);

    // loop through each input, if optional make sure it isnt required and go to the next input
    inputs.forEach(input => {
      if (this.isOptionalInput(input)) {
        input.removeAttribute('required');
        return;
      }

      // is not optional
      input.required = true;
      input.validationMessage = 'Required';

      if (typeof input.reportValidity === "function") {
        let inputValid = input.reportValidity();
        if (inputValid) {
          input.validationMessage = "";
        }
        isValid = inputValid && isValid;
      }
    });

    return isValid;
  }

  /**
   * check if the input we pass in is a part of the optionalFields array
   * @param input
   * @return {boolean}
   */
  isOptionalInput(input) {
    if (input === this.$['lookupAddressInput']) {
      return true;
    }

    for (let field of this.optionalFields) {
      // get the element from the array key we pass in
      let inputToCheck = this.$[`${field}Input`];
      if (input === inputToCheck) {
        // is in the optional fields array
        return true;
      }
    }

    // is not optional
    return false;
  }

  __handleValidFields() {
    let inputs = Object.values(this.$);
    inputs = [].slice.call(inputs, 1);

    inputs.forEach(input => {
      if (this.isOptionalInput(input)) {
        input.removeAttribute('required');
        return;
      }

      // is not optional
      input.required = true;
    });
  }

  resetValidity() {
    let inputsToReset = this.root.querySelectorAll('cbar-textfield');

    Array.from(inputsToReset).forEach(input => {
      let inputIsRequired = input.required;

      input.required = false;
      requestAnimationFrame(() => {
        input.required = inputIsRequired;
        input.validationMessage = "";
      });
    });
  }
}

window.customElements.define('cbar-address-autocomplete', CbarAddressAutocomplete);
